'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { useEscapeDismiss } from './dismiss-stack';
import { useShortcutLabel } from '../lib/shortcut-label';
import { cn } from '../utils/cn';

import { buttonVariants } from './button';
import { PopoverContent, PopoverDivider, PopoverItem, popoverItemToneClass } from './popover';

// ---------------------------------------------------------------------------
// ContextMenu — portal-based container at mouse coordinates
// ---------------------------------------------------------------------------
interface ContextMenuProps {
	open: boolean;
	position: { x: number; y: number };
	onClose: () => void;
	children: React.ReactNode;
	className?: string;
}

export const ContextMenu = ({
	open,
	position,
	onClose,
	children,
	className
}: ContextMenuProps) => {
	const menuRef = useRef<HTMLDivElement>(null);
	const [finalPos, setFinalPos] = useState<{ x: number; y: number } | null>(null);

	// Reset finalPos synchronously during render when position changes,
	// so the stale clamped position is never painted.
	const prevPositionRef = useRef(position);
	if (position.x !== prevPositionRef.current.x || position.y !== prevPositionRef.current.y) {
		prevPositionRef.current = position;
		if (finalPos !== null) {
			setFinalPos(null);
		}
	}

	// Custom dismiss: check data-ctx-menu attribute so submenu portal clicks
	// are not treated as "outside" (submenu portals are separate DOM trees).
	useEffect(() => {
		if (!open) return;

		const handlePointerDown = (e: PointerEvent) => {
			const target = e.target as HTMLElement;
			if (target.closest?.('[data-ctx-menu]')) return;
			onClose();
		};

		const tid = setTimeout(() => document.addEventListener('pointerdown', handlePointerDown, true), 0);
		return () => {
			clearTimeout(tid);
			document.removeEventListener('pointerdown', handlePointerDown, true);
		};
	}, [open, onClose]);

	useEscapeDismiss(open, onClose);

	// Measure and clamp to viewport after the hidden frame renders
	useEffect(() => {
		if (!open) {
			setFinalPos(null);
			return;
		}

		const raf = requestAnimationFrame(() => {
			const el = menuRef.current;
			if (!el) return;

			const rect = el.getBoundingClientRect();
			const padding = 8;
			const x = Math.min(position.x, window.innerWidth - rect.width - padding);
			const y = Math.min(position.y, window.innerHeight - rect.height - padding);

			setFinalPos({ x: Math.max(padding, x), y: Math.max(padding, y) });
		});

		return () => cancelAnimationFrame(raf);
	}, [open, position]);

	if (!open || typeof document === 'undefined') return null;

	// Before clamping: render hidden at raw position for measurement.
	// After clamping: render visible at final position with fade-in.
	const ready = finalPos !== null;

	return createPortal(
		<div
			ref={menuRef}
			data-ctx-menu
			className={cn('fixed z-[9999]', className)}
			style={{
				top: ready ? finalPos.y : position.y,
				left: ready ? finalPos.x : position.x,
				visibility: ready ? 'visible' : 'hidden'
			}}
			onClick={(e) => e.stopPropagation()}
		>
			<PopoverContent size="md" className={cn('overflow-visible py-2', className)}>
				{children}
			</PopoverContent>
		</div>,
		document.body
	);
};

// ---------------------------------------------------------------------------
// ContextMenuItem — menu item button with optional shortcut display
// ---------------------------------------------------------------------------
interface ContextMenuItemProps {
	children: React.ReactNode;
	onClick?: () => void;
	commandId?: string;
	variant?: 'default' | 'danger' | 'active';
}

export const ContextMenuItem = ({
	children,
	onClick,
	commandId,
	variant,
}: ContextMenuItemProps) => {
	const resolveShortcutLabel = useShortcutLabel();

	const shortcutLabel = commandId ? resolveShortcutLabel(commandId) : null;

	return (
		<PopoverItem onClick={onClick} variant={variant}>
			{children}
			{shortcutLabel && (
				<kbd className="ml-auto font-sans text-[12px] font-medium tracking-widest text-neutral-muted">
					{shortcutLabel}
				</kbd>
			)}
		</PopoverItem>
	);
};

// ---------------------------------------------------------------------------
// ContextMenuSeparator — thin divider
// ---------------------------------------------------------------------------
export const ContextMenuSeparator = () => {
	return <PopoverDivider />;
};

// ---------------------------------------------------------------------------
// ContextMenuSub — hover-to-open submenu with viewport-aware positioning
// ---------------------------------------------------------------------------
interface ContextMenuSubProps {
	label: React.ReactNode;
	icon?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}

export const ContextMenuSub = ({ label, icon, children, className }: ContextMenuSubProps) => {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

	const clearTimers = useCallback(() => {
		if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
		if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
	}, []);

	const handleEnter = useCallback(() => {
		clearTimers();
		openTimer.current = setTimeout(() => setOpen(true), 100);
	}, [clearTimers]);

	const handleLeave = useCallback(() => {
		clearTimers();
		closeTimer.current = setTimeout(() => setOpen(false), 100);
	}, [clearTimers]);

	// Position the portal-based submenu next to the trigger
	useEffect(() => {
		if (!open) {
			setPanelPos(null);
			return;
		}

		const raf = requestAnimationFrame(() => {
			const trigger = triggerRef.current;
			const panel = panelRef.current;
			if (!trigger || !panel) return;

			const triggerRect = trigger.getBoundingClientRect();
			const panelWidth = panel.offsetWidth;
			const padding = 8;

			const flipLeft = triggerRect.right + panelWidth + padding > window.innerWidth;
			const left = flipLeft
				? triggerRect.left - panelWidth
				: triggerRect.right;

			setPanelPos({ top: triggerRect.top, left });
		});

		return () => cancelAnimationFrame(raf);
	}, [open]);

	// Cleanup timers on unmount
	useEffect(() => clearTimers, [clearTimers]);

	return (
		<div
			ref={triggerRef}
			onMouseEnter={handleEnter}
			onMouseLeave={handleLeave}
		>
			{/* Trigger row — same size as PopoverItem's default (`list-sm`) so a
			    submenu row lines up with its sibling items in icon and text size */}
			<div
				className={cn(
					buttonVariants({ variant: 'ghost-muted', size: 'list-sm' }),
					popoverItemToneClass.default,
					className
				)}
			>
				{icon}
				<span className="flex-1">{label}</span>
				<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</div>

			{/* Submenu panel — portaled so backdrop-blur works independently */}
			{open && typeof document !== 'undefined' && createPortal(
				<div
					ref={panelRef}
					data-ctx-menu
					className="fixed z-[9999]"
					style={{
						top: panelPos?.top ?? 0,
						left: panelPos?.left ?? 0,
						visibility: panelPos ? 'visible' : 'hidden'
					}}
					onMouseEnter={handleEnter}
					onMouseLeave={handleLeave}
					onClick={(e) => e.stopPropagation()}
				>
					<PopoverContent size="md" className="py-2">
						{children}
					</PopoverContent>
				</div>,
				document.body
			)}
		</div>
	);
};
